import * as z from "zod";

const SentimentBreakdown_ = z.object({
  positive: z.number(),
  neutral: z.number(),
  negative: z.number(),
});

export const SentimentBreakdown: z.ZodType<SentimentBreakdown> =
  SentimentBreakdown_;
export interface SentimentBreakdown extends z.infer<
  typeof SentimentBreakdown_
> {}

const DailySentimentAnalytics_ = z.object({
  date: z.string(), // ISO date string (YYYY-MM-DD)
  sentimentBreakdown: SentimentBreakdown,
  totalConversations: z.number(),
});

export const DailySentimentAnalytics: z.ZodType<DailySentimentAnalytics> =
  DailySentimentAnalytics_;
export interface DailySentimentAnalytics extends z.infer<
  typeof DailySentimentAnalytics_
> {}

const CategoryBreakdown_ = z.record(z.string(), z.number());

export const CategoryBreakdown: z.ZodType<CategoryBreakdown> =
  CategoryBreakdown_;
export interface CategoryBreakdown extends z.infer<typeof CategoryBreakdown_> {}

const DailyCategoryAnalytics_ = z.object({
  date: z.string(), // ISO date string (YYYY-MM-DD)
  categoryBreakdown: CategoryBreakdown,
  totalConversations: z.number(),
});

export const DailyCategoryAnalytics: z.ZodType<DailyCategoryAnalytics> =
  DailyCategoryAnalytics_;
export interface DailyCategoryAnalytics extends z.infer<
  typeof DailyCategoryAnalytics_
> {}

const SentimentTotals_ = z.object({
  totalPositive: z.number(),
  totalNeutral: z.number(),
  totalNegative: z.number(),
});

export const SentimentTotals: z.ZodType<SentimentTotals> = SentimentTotals_;
export interface SentimentTotals extends z.infer<typeof SentimentTotals_> {}

const CategoryTotals_ = z.record(z.string(), z.number());

export const CategoryTotals: z.ZodType<CategoryTotals> = CategoryTotals_;
export interface CategoryTotals extends z.infer<typeof CategoryTotals_> {}

const DailyToolCallAnalytics_ = z.object({
  date: z.string(), // ISO date string (YYYY-MM-DD)
  toolCallSuccesses: z.number(),
  toolCallErrors: z.number(),
  totalToolCalls: z.number(),
  totalConversations: z.number(),
});

export const DailyToolCallAnalytics: z.ZodType<DailyToolCallAnalytics> =
  DailyToolCallAnalytics_;
export interface DailyToolCallAnalytics extends z.infer<
  typeof DailyToolCallAnalytics_
> {}

const ToolCallTotals_ = z.object({
  totalSuccesses: z.number(),
  totalErrors: z.number(),
  totalToolCalls: z.number(),
});

export const ToolCallTotals: z.ZodType<ToolCallTotals> = ToolCallTotals_;
export interface ToolCallTotals extends z.infer<typeof ToolCallTotals_> {}

const DailyToolAnalytics_ = z.object({
  date: z.string(), // ISO date string (YYYY-MM-DD)
  toolAnalysis: z.record(
    z.string(),
    z.object({
      total: z.number(),
      successes: z.number(),
      errorCount: z.number(),
    }),
  ),
  totalConversations: z.number(),
});

export const DailyToolAnalytics: z.ZodType<DailyToolAnalytics> =
  DailyToolAnalytics_;
export interface DailyToolAnalytics extends z.infer<
  typeof DailyToolAnalytics_
> {}

const ToolAnalysisTotals_ = z.record(
  z.string(),
  z.object({
    total: z.number(),
    successes: z.number(),
    errorCount: z.number(),
  }),
);

export const ToolAnalysisTotals: z.ZodType<ToolAnalysisTotals> =
  ToolAnalysisTotals_;
export interface ToolAnalysisTotals extends z.infer<
  typeof ToolAnalysisTotals_
> {}

const DailyAssigneeSummaryAnalytics_ = z.object({
  date: z.string(), // ISO date string (YYYY-MM-DD)
  assigneeSummaryBreakdown: z.record(z.string(), z.number()),
  totalConversations: z.number(),
});

export const DailyAssigneeSummaryAnalytics: z.ZodType<DailyAssigneeSummaryAnalytics> =
  DailyAssigneeSummaryAnalytics_;
export interface DailyAssigneeSummaryAnalytics extends z.infer<
  typeof DailyAssigneeSummaryAnalytics_
> {}

const AssigneeSummaryTotals_ = z.record(z.string(), z.number());

export const AssigneeSummaryTotals: z.ZodType<AssigneeSummaryTotals> =
  AssigneeSummaryTotals_;
export interface AssigneeSummaryTotals extends z.infer<
  typeof AssigneeSummaryTotals_
> {}

const DailyHandoffAnalytics_ = z.object({
  date: z.string(), // ISO date string (YYYY-MM-DD)
  handoffBySource: z.record(z.string(), z.number()),
  totalHandoffs: z.number(),
});

export const DailyHandoffAnalytics: z.ZodType<DailyHandoffAnalytics> =
  DailyHandoffAnalytics_;
export interface DailyHandoffAnalytics extends z.infer<
  typeof DailyHandoffAnalytics_
> {}

const HandoffBySourceTotals_ = z.record(z.string(), z.number());

export const HandoffBySourceTotals: z.ZodType<HandoffBySourceTotals> =
  HandoffBySourceTotals_;
export interface HandoffBySourceTotals extends z.infer<
  typeof HandoffBySourceTotals_
> {}

const HandoffBySourceAndCategoryTotals_ = z.record(
  z.string(),
  z.record(z.string(), z.number()),
);

export const HandoffBySourceAndCategoryTotals: z.ZodType<HandoffBySourceAndCategoryTotals> =
  HandoffBySourceAndCategoryTotals_;
export interface HandoffBySourceAndCategoryTotals extends z.infer<
  typeof HandoffBySourceAndCategoryTotals_
> {}

const HandoffSuggestedToolTotals_ = z.record(
  z.string(),
  z.object({ total: z.number(), enabled: z.number(), disabled: z.number() }),
);

export const HandoffSuggestedToolTotals: z.ZodType<HandoffSuggestedToolTotals> =
  HandoffSuggestedToolTotals_;
export interface HandoffSuggestedToolTotals extends z.infer<
  typeof HandoffSuggestedToolTotals_
> {}

const DailyHandoffSuggestedToolAnalytics_ = z.object({
  date: z.string(),
  suggestedToolBreakdown: z.record(
    z.string(),
    z.object({ total: z.number(), enabled: z.number(), disabled: z.number() }),
  ),
  totalHandoffsWithSuggestedTool: z.number(),
});

export const DailyHandoffSuggestedToolAnalytics: z.ZodType<DailyHandoffSuggestedToolAnalytics> =
  DailyHandoffSuggestedToolAnalytics_;
export interface DailyHandoffSuggestedToolAnalytics extends z.infer<
  typeof DailyHandoffSuggestedToolAnalytics_
> {}

const HandoffSuggestedToolEnabledDisabledTotals_ = z.object({
  totalEnabled: z.number(),
  totalDisabled: z.number(),
});

export const HandoffSuggestedToolEnabledDisabledTotals: z.ZodType<HandoffSuggestedToolEnabledDisabledTotals> =
  HandoffSuggestedToolEnabledDisabledTotals_;
export interface HandoffSuggestedToolEnabledDisabledTotals extends z.infer<
  typeof HandoffSuggestedToolEnabledDisabledTotals_
> {}

const CategorySentimentCombinedTotals_ = z.record(
  z.string(),
  z.object({
    positive: z.number(),
    neutral: z.number(),
    negative: z.number(),
    total: z.number(),
  }),
);

export const CategorySentimentCombinedTotals: z.ZodType<CategorySentimentCombinedTotals> =
  CategorySentimentCombinedTotals_;
export interface CategorySentimentCombinedTotals extends z.infer<
  typeof CategorySentimentCombinedTotals_
> {}

const ToolCallSentimentCombinedTotals_ = z.record(
  z.string(),
  z.object({
    positive: z.number(),
    neutral: z.number(),
    negative: z.number(),
    total: z.number(),
  }),
);

export const ToolCallSentimentCombinedTotals: z.ZodType<ToolCallSentimentCombinedTotals> =
  ToolCallSentimentCombinedTotals_;
export interface ToolCallSentimentCombinedTotals extends z.infer<
  typeof ToolCallSentimentCombinedTotals_
> {}

const ToolErrorBreakdownTotals_ = z.record(
  z.string(),
  z.record(z.string(), z.number()),
);

export const ToolErrorBreakdownTotals: z.ZodType<ToolErrorBreakdownTotals> =
  ToolErrorBreakdownTotals_;
export interface ToolErrorBreakdownTotals extends z.infer<
  typeof ToolErrorBreakdownTotals_
> {}

const CurrencyBreakdown_ = z.record(
  z.string(),
  z.object({ totalValue: z.number(), orderCount: z.number() }),
);

export const CurrencyBreakdown: z.ZodType<CurrencyBreakdown> =
  CurrencyBreakdown_;
export interface CurrencyBreakdown extends z.infer<typeof CurrencyBreakdown_> {}

const DailyOrderAnalytics_ = z.object({
  date: z.string(), // ISO date string (YYYY-MM-DD)
  totalOrderValue: z.number(),
  numberOfOrders: z.number(),
  averageOrderValue: z.number(),
  currencyBreakdown: CurrencyBreakdown,
  totalConversations: z.number(),
});

export const DailyOrderAnalytics: z.ZodType<DailyOrderAnalytics> =
  DailyOrderAnalytics_;
export interface DailyOrderAnalytics extends z.infer<
  typeof DailyOrderAnalytics_
> {}

const OrderAnalyticsTotals_ = z.object({
  totalOrderValue: z.number(),
  numberOfOrders: z.number(),
  averageOrderValue: z.number(),
  currencyBreakdown: CurrencyBreakdown,
});

export const OrderAnalyticsTotals: z.ZodType<OrderAnalyticsTotals> =
  OrderAnalyticsTotals_;
export interface OrderAnalyticsTotals extends z.infer<
  typeof OrderAnalyticsTotals_
> {}

const TagBreakdown_ = z.record(z.string(), z.number());

export const TagBreakdown: z.ZodType<TagBreakdown> = TagBreakdown_;
export interface TagBreakdown extends z.infer<typeof TagBreakdown_> {}

const DailyTagAnalytics_ = z.object({
  date: z.string(), // ISO date string (YYYY-MM-DD)
  tagBreakdown: TagBreakdown,
  totalConversations: z.number(),
});

export const DailyTagAnalytics: z.ZodType<DailyTagAnalytics> =
  DailyTagAnalytics_;
export interface DailyTagAnalytics extends z.infer<typeof DailyTagAnalytics_> {}

const TagTotals_ = z.record(z.string(), z.number());

export const TagTotals: z.ZodType<TagTotals> = TagTotals_;
export interface TagTotals extends z.infer<typeof TagTotals_> {}

const TagOrderAnalyticsTotals_ = z.record(
  z.string(),
  z.object({
    totalOrderValue: z.number(),
    numberOfOrders: z.number(),
    averageOrderValue: z.number(),
    conversationsWithOrders: z.number(),
    totalConversations: z.number(),
    conversionRate: z.number(),
  }),
);

export const TagOrderAnalyticsTotals: z.ZodType<TagOrderAnalyticsTotals> =
  TagOrderAnalyticsTotals_;
export interface TagOrderAnalyticsTotals extends z.infer<
  typeof TagOrderAnalyticsTotals_
> {}

const ToolOrderAnalyticsTotals_ = z.record(
  z.string(),
  z.object({
    totalOrderValue: z.number(),
    numberOfOrders: z.number(),
    averageOrderValue: z.number(),
    conversationsWithOrders: z.number(),
    totalConversations: z.number(),
    conversionRate: z.number(),
  }),
);

export const ToolOrderAnalyticsTotals: z.ZodType<ToolOrderAnalyticsTotals> =
  ToolOrderAnalyticsTotals_;
export interface ToolOrderAnalyticsTotals extends z.infer<
  typeof ToolOrderAnalyticsTotals_
> {}

const SentimentOrderAnalyticsTotals_ = z.record(
  z.string(),
  z.object({
    totalOrderValue: z.number(),
    numberOfOrders: z.number(),
    averageOrderValue: z.number(),
    conversationsWithOrders: z.number(),
    totalConversations: z.number(),
    conversionRate: z.number(),
  }),
);

export const SentimentOrderAnalyticsTotals: z.ZodType<SentimentOrderAnalyticsTotals> =
  SentimentOrderAnalyticsTotals_;
export interface SentimentOrderAnalyticsTotals extends z.infer<
  typeof SentimentOrderAnalyticsTotals_
> {}

const OverallOrderAnalyticsTotals_ = z.object({
  totalOrderValue: z.number(),
  numberOfOrders: z.number(),
  averageOrderValue: z.number(),
  conversationsWithOrders: z.number(),
  totalConversations: z.number(),
  conversionRate: z.number(),
});

export const OverallOrderAnalyticsTotals: z.ZodType<OverallOrderAnalyticsTotals> =
  OverallOrderAnalyticsTotals_;
export interface OverallOrderAnalyticsTotals extends z.infer<
  typeof OverallOrderAnalyticsTotals_
> {}

const ToolCallOrderAnalyticsTotals_ = z.object({
  success: z.object({
    totalOrderValue: z.number(),
    numberOfOrders: z.number(),
    averageOrderValue: z.number(),
    conversationsWithOrders: z.number(),
    totalConversations: z.number(),
    conversionRate: z.number(),
  }),
  error: z.object({
    totalOrderValue: z.number(),
    numberOfOrders: z.number(),
    averageOrderValue: z.number(),
    conversationsWithOrders: z.number(),
    totalConversations: z.number(),
    conversionRate: z.number(),
  }),
});

export const ToolCallOrderAnalyticsTotals: z.ZodType<ToolCallOrderAnalyticsTotals> =
  ToolCallOrderAnalyticsTotals_;
export interface ToolCallOrderAnalyticsTotals extends z.infer<
  typeof ToolCallOrderAnalyticsTotals_
> {}

const ConversationInsightsAnalyticsData_ = z.object({
  sentimentByDay: z.array(DailySentimentAnalytics),
  conversationCategoriesByDay: z.array(DailyCategoryAnalytics),
  toolCallAnalyticsByDay: z.array(DailyToolCallAnalytics),
  toolAnalyticsByDay: z.array(DailyToolAnalytics),
  assigneeSummaryByDay: z.array(DailyAssigneeSummaryAnalytics),
  handoffByDay: z.array(DailyHandoffAnalytics),
  handoffSuggestedToolByDay: z.array(DailyHandoffSuggestedToolAnalytics),
  orderAnalyticsByDay: z.array(DailyOrderAnalytics),
  tagsByDay: z.array(DailyTagAnalytics),
  sentimentTotals: SentimentTotals,
  conversationCategoriesTotals: CategoryTotals,
  toolCallTotals: ToolCallTotals,
  toolAnalysisTotals: ToolAnalysisTotals,
  assigneeSummaryTotals: AssigneeSummaryTotals,
  categorySentimentCombinedTotals: CategorySentimentCombinedTotals,
  toolCallSentimentCombinedTotals: ToolCallSentimentCombinedTotals,
  toolErrorBreakdownTotals: ToolErrorBreakdownTotals,
  handoffBySourceTotals: HandoffBySourceTotals,
  handoffBySourceAndCategoryTotals: HandoffBySourceAndCategoryTotals,
  handoffSuggestedToolTotals: HandoffSuggestedToolTotals,
  handoffSuggestedToolEnabledDisabledTotals:
    HandoffSuggestedToolEnabledDisabledTotals,
  tagOrderAnalyticsTotals: TagOrderAnalyticsTotals,
  toolOrderAnalyticsTotals: ToolOrderAnalyticsTotals,
  sentimentOrderAnalyticsTotals: SentimentOrderAnalyticsTotals,
  overallOrderAnalyticsTotals: OverallOrderAnalyticsTotals,
  toolCallOrderAnalyticsTotals: ToolCallOrderAnalyticsTotals,
});

export const ConversationInsightsAnalyticsData: z.ZodType<ConversationInsightsAnalyticsData> =
  ConversationInsightsAnalyticsData_;
export interface ConversationInsightsAnalyticsData extends z.infer<
  typeof ConversationInsightsAnalyticsData_
> {}
